import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Divider,
  useToast,
  Flex,
  Image,
  Badge,
  useColorModeValue,
  FormErrorMessage,
  SimpleGrid,
  Icon
} from '@chakra-ui/react';
import { FaShieldAlt, FaGolfBall } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { processPayment, createPaymentIntent } from '../apis/paymentApi';
import { useAuth } from '../context/AuthContext';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

// Base URL for API calls - use environment variable or fallback to localhost for development
const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_BASE_URL || 'https://apidev.birdie.house')
  : 'http://localhost:3001'; // Use your local backend port

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, loading } = useAuth();
  const userData = location.state || {};
  
  // Stripe hooks
  const stripe = useStripe();
  const elements = useElements();
  
  const [paymentInfo, setPaymentInfo] = useState({
    name: userData.name || '',
    email: userData.email || '',
  });
  
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Redirect if no user data is available
  useEffect(() => {
    console.log("user from payment", user, "loading:", loading);
    if (!loading && !user) {
      toast({
        title: 'Error',
        description: 'You must be logged in or registered to access payments.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/auth');
    }
  }, [user, loading, navigate, toast]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setPaymentInfo({
      ...paymentInfo,
      [name]: value,
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    if (!paymentInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Validate email
    if (!paymentInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(paymentInfo.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      toast({
        title: 'Please wait',
        description: 'Payment system is still loading.',
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please check your payment information and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    // Validate user ID is available
    if (!user || !user.id) {
      toast({
        title: 'Authentication Error',
        description: 'User information is missing. Please log in again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/auth');
      return;
    }
    
    // Log user data for debugging
    console.log('Payment data:', {
      userId: user.id,
      membershipType: userData.membershipType,
      amount: userData.amount || 250.00,
      name: paymentInfo.name,
      email: paymentInfo.email
    });
    
    setIsProcessing(true);
    
    try {
      // 1. Create a payment intent on the server
      console.log('Creating payment intent...');
      const intentResponse = await createPaymentIntent(
        user.id,
        userData.membershipType,
        userData.amount || 250.00
      );
      
      if (!intentResponse || !intentResponse.clientSecret) {
        throw new Error('Failed to create payment intent. Please try again.');
      }
      
      const { clientSecret, paymentIntentId } = intentResponse;
      
      // 2. Confirm the payment with Stripe.js
      console.log('Confirming payment...');
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: paymentInfo.name,
            email: paymentInfo.email,
          },
        }
      });
      
      if (error) {
        console.error('Payment confirmation error:', error);
        throw new Error(error.message);
      }
      
      console.log('Payment confirmation result:', paymentIntent);
      
      // 3. Verify the payment was successful
      if (paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded, updating backend...');
        
        // The payment was successful, now update the backend
        const confirmResponse = await axios.post(`${API_URL}/api/confirm-payment`, {
          paymentIntentId: paymentIntent.id,
          membershipDetails: {
            userId: user.id,
            membershipType: userData.membershipType,
            amount: userData.amount || 250.00,
            paymentMethodId: paymentIntent.payment_method
          }
        }, {
          withCredentials: true
        });
        
        console.log('Backend update response:', confirmResponse.data);
        
        if (!confirmResponse.data.success) {
          throw new Error('Payment was processed but membership activation failed.');
        }
        
        toast({
          title: 'Payment Successful',
          description: 'Your membership has been activated successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
        
        // Redirect to verification page after successful payment
        navigate('/verify-register', { 
          state: { 
            email: userData.email,
            redirectPath: '/dashboard',
            paymentComplete: true
          } 
        });
      } else if (paymentIntent.status === 'requires_payment_method') {
        // This means the card was declined
        console.log('Card was declined:', paymentIntent);
        throw new Error('Your card was declined. Please try a different payment method.');
      } else {
        console.log('Unexpected payment status:', paymentIntent.status);
        throw new Error(`Payment not successful. Status: ${paymentIntent.status}`);
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Failed',
        description: error.message || 'There was an error processing your payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      p={{ base: 1, md: 2 }}
    >
      <Container maxW={{ base: "100%", md: "container.md" }} p={0}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0} overflow="hidden" borderRadius="lg">
          {/* Left side - Payment form */}
          <Box
            bg={bgColor}
            p={{ base: 4, md: 6 }}
            borderWidth="1px"
            borderColor={borderColor}
            borderRightWidth={{ base: '1px', md: '0' }}
            borderBottomWidth={{ base: '0', md: '1px' }}
            borderTopLeftRadius="lg"
            borderBottomLeftRadius={{ base: '0', md: 'lg' }}
            borderTopRightRadius={{ base: 'lg', md: '0' }}
          >
            <VStack align="flex-start" spacing={4}>
              <Heading size="lg" color="#82ca9c">Complete Your Payment</Heading>
              <Text fontSize="sm" color="gray.600">
                Please enter your payment details to activate your Unlimited Membership
              </Text>
              
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <VStack spacing={3} align="stretch">
                  <FormControl isInvalid={errors.name}>
                    <FormLabel fontSize="xs" fontWeight="medium">Cardholder Name</FormLabel>
                    <Input
                      name="name"
                      value={paymentInfo.name}
                      onChange={handleInputChange}
                      placeholder="Name on card"
                      size="sm"
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                    <FormErrorMessage fontSize="2xs">{errors.name}</FormErrorMessage>
                  </FormControl>
                  
                  <FormControl isInvalid={errors.email}>
                    <FormLabel fontSize="xs" fontWeight="medium">Email</FormLabel>
                    <Input
                      name="email"
                      value={paymentInfo.email}
                      onChange={handleInputChange}
                      placeholder="Email address"
                      size="sm"
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                    <FormErrorMessage fontSize="2xs">{errors.email}</FormErrorMessage>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="medium">Card Details</FormLabel>
                    <Box
                      p={2}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="gray.200"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    >
                      <CardElement 
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#9e2146',
                            },
                          },
                        }}
                      />
                    </Box>
                  </FormControl>
                  
                  {process.env.NODE_ENV !== 'production' && (
                    <Box 
                      mt={2} 
                      p={2} 
                      bg="blue.50" 
                      borderRadius="md" 
                      fontSize="xs"
                    >
                      <Text fontWeight="bold" mb={1}>Test Card Information:</Text>
                      <Text>Success: 4242 4242 4242 4242</Text>
                      <Text>Decline: 4000 0000 0000 0002</Text>
                      <Text>Any future expiry date, any 3-digit CVC, any 5-digit ZIP</Text>
                    </Box>
                  )}
                  
                  <Button
                    type="submit"
                    w="full"
                    mt={2}
                    py={4}
                    bg="#82ca9c"
                    color="white"
                    fontWeight="bold"
                    fontSize="sm"
                    borderRadius="md"
                    _hover={{ bg: "#6db889" }}
                    _active={{ bg: "#5ba97c" }}
                    boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
                    isLoading={isProcessing}
                    loadingText="Processing"
                    disabled={!stripe}
                    size="sm"
                  >
                    Pay ${userData.amount?.toFixed(2) || '250.00'}
                  </Button>
                  
                  <HStack justify="center" fontSize="xs" color="gray.500" mt={2}>
                    <Icon as={FaShieldAlt} />
                    <Text>Secure payment processing</Text>
                  </HStack>
                </VStack>
              </form>
            </VStack>
          </Box>
          
          {/* Right side - Order summary */}
          <Box
            bg="gray.50"
            p={{ base: 4, md: 6 }}
            borderWidth="1px"
            borderColor={borderColor}
            borderLeftWidth={{ base: '1px', md: '0' }}
            borderTopWidth={{ base: '0', md: '1px' }}
            borderBottomRightRadius="lg"
            borderBottomLeftRadius={{ base: 'lg', md: '0' }}
            borderTopRightRadius={{ base: '0', md: 'lg' }}
          >
            <VStack align="stretch" spacing={4}>
              <Heading size="md" color="gray.700">Order Summary</Heading>
              
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                bg={bgColor}
              >
                <HStack>
                  <Box
                    bg="rgba(130, 202, 156, 0.1)"
                    borderRadius="md"
                    p={2}
                    mr={3}
                  >
                    <Icon as={FaGolfBall} color="#82ca9c" boxSize={5} />
                  </Box>
                  <Box flex="1">
                    <HStack mb={1}>
                      <Text fontWeight="medium" fontSize="sm">Unlimited Membership</Text>
                      <Badge colorScheme="purple" fontSize="2xs" px={1} py={0} borderRadius="full">
                        Best Value
                      </Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Unlimited access to all indoor golf facilities
                    </Text>
                  </Box>
                </HStack>
              </Box>
              
              <Divider />
              
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Membership Fee</Text>
                  <Text fontSize="sm" fontWeight="medium">${userData.amount?.toFixed(2) || '250.00'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Tax</Text>
                  <Text fontSize="sm" fontWeight="medium">$0.00</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between" pt={2}>
                  <Text fontSize="sm" fontWeight="bold">Total</Text>
                  <Text fontSize="md" fontWeight="bold" color="#82ca9c">${userData.amount?.toFixed(2) || '250.00'}</Text>
                </HStack>
              </VStack>
              
              <Box
                bg="rgba(130, 202, 156, 0.05)"
                borderRadius="md"
                p={3}
                borderWidth="1px"
                borderColor="rgba(130, 202, 156, 0.2)"
                mt={2}
              >
                <VStack align="stretch" spacing={2}>
                  <Heading size="xs" color="gray.700">Membership Benefits:</Heading>
                  <HStack fontSize="xs" color="gray.600">
                    <Box w="4px" h="4px" borderRadius="full" bg="#82ca9c" />
                    <Text>Unlimited access to all facilities</Text>
                  </HStack>
                  <HStack fontSize="xs" color="gray.600">
                    <Box w="4px" h="4px" borderRadius="full" bg="#82ca9c" />
                    <Text>Priority bay reservations</Text>
                  </HStack>
                  <HStack fontSize="xs" color="gray.600">
                    <Box w="4px" h="4px" borderRadius="full" bg="#82ca9c" />
                    <Text>Free equipment rentals</Text>
                  </HStack>
                  <HStack fontSize="xs" color="gray.600">
                    <Box w="4px" h="4px" borderRadius="full" bg="#82ca9c" />
                    <Text>Access to exclusive events</Text>
                  </HStack>
                </VStack>
              </Box>
              
              <HStack spacing={2} mt={2} justify="center">
                <Image 
                  src="https://cdn.iconscout.com/icon/free/png-256/visa-3-226460.png" 
                  alt="Visa" 
                  boxSize="24px"
                  objectFit="contain"
                />
                <Image 
                  src="https://cdn.iconscout.com/icon/free/png-256/mastercard-6-226423.png" 
                  alt="Mastercard" 
                  boxSize="24px"
                  objectFit="contain"
                />
                <Image 
                  src="https://cdn.iconscout.com/icon/free/png-256/american-express-6-226419.png" 
                  alt="American Express" 
                  boxSize="24px"
                  objectFit="contain"
                />
                <Image 
                  src="https://cdn.iconscout.com/icon/free/png-256/discover-3-226449.png" 
                  alt="Discover" 
                  boxSize="24px"
                  objectFit="contain"
                />
              </HStack>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>
    </Flex>
  );
};

export default Payment; 